/**
 * Route-level access decision for B2C (personal user) pages.
 *
 * B2C and enterprise logins share one JWT in localStorage (see lib/auth.ts), so the account
 * type comes from the token's `role` / `identity_type` claims. Enterprise owners and recruiters
 * must never see B2C pages; hiding nav links is not enough because the URL can be typed or
 * pasted directly.
 *
 * Two layers enforce this:
 * - `components/B2CRouteGuard.tsx` wraps page content in the root layout and keeps the page
 *   unmounted until this decision allows it (covers client navigation and hard loads).
 * - `buildPreHydrationB2CRouteGuardScript()` in lib/b2cOnlyRoutes.ts runs in <head> before first
 *   paint on hard loads, hiding the document and redirecting enterprise sessions early.
 *
 * The backend remains the security boundary: `get_current_user` returns 403 for enterprise
 * persona tokens on the endpoints these pages call. This module is the UX layer on top.
 */

import { decodeToken } from '@/lib/auth'
import {
  ENTERPRISE_LANDING_PATH,
  findB2COnlyRoute,
  isEnterprisePersonaJwtRole,
  UNKNOWN_PERSONA_FALLBACK_PATH,
} from '@/lib/b2cOnlyRoutes'

export type B2CRouteAccess =
  /** B2C session, or no session at all (existing sign-in handling applies unchanged). */
  | { kind: 'allow' }
  /** Authenticated session that must not see this page; navigate to `destination`. */
  | { kind: 'redirect'; destination: string }
  /**
   * Enterprise candidate: keep the page hidden and let EnterpriseInterviewLockGuard own the
   * navigation to /interview, so the two guards never race to different destinations.
   */
  | { kind: 'hold' }
  /** Token present but undecodable; treat like a backend 401 (existing auth-failure flow). */
  | { kind: 'invalid-session' }

type B2CRelevantJwtClaims = { role?: unknown; identity_type?: unknown }

/**
 * Access decision for a B2C-only route given the raw token from storage.
 * Fails closed: only an explicit personal-user token (role "user" or the legacy missing role)
 * is allowed through.
 */
export function resolveB2CRouteAccess(authToken: string | null): B2CRouteAccess {
  if (!authToken) return { kind: 'allow' }

  const payload = decodeToken(authToken) as B2CRelevantJwtClaims | null
  if (!payload || typeof payload !== 'object') return { kind: 'invalid-session' }

  if (payload.identity_type === 'enterprise_candidate') return { kind: 'hold' }

  const role = payload.role
  // Same personal-user rule as isNormalUserSession(): role "user", or missing on legacy tokens.
  if (!role || role === 'user') return { kind: 'allow' }
  if (typeof role === 'string' && isEnterprisePersonaJwtRole(role)) {
    return { kind: 'redirect', destination: ENTERPRISE_LANDING_PATH }
  }
  return { kind: 'redirect', destination: UNKNOWN_PERSONA_FALLBACK_PATH }
}

export type B2CRouteGuardView = {
  /** Whether the page itself may render (false → neutral pending screen instead). */
  renderPage: boolean
  /** Access decision; null when not on a B2C-only route or before client mount. */
  routeAccess: B2CRouteAccess | null
}

/**
 * What B2CRouteGuard renders. Before mount (server render + hydration) localStorage is not
 * readable, so restricted routes render a pending screen unless they are public/SEO routes
 * that keep their server HTML (enterprise sessions there are handled by the pre-paint script).
 */
export function resolveB2CRouteGuardView(
  pathname: string | null | undefined,
  hasMounted: boolean,
  readAuthToken: () => string | null,
): B2CRouteGuardView {
  const restrictedRoute = findB2COnlyRoute(pathname)
  if (!restrictedRoute) return { renderPage: true, routeAccess: null }
  if (!hasMounted) {
    return { renderPage: restrictedRoute.preserveServerRender, routeAccess: null }
  }
  const routeAccess = resolveB2CRouteAccess(readAuthToken())
  return { renderPage: routeAccess.kind === 'allow', routeAccess }
}
